import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { money, formatDateAT } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { AccessDenied, canAccessHQ } from "@/lib/permissions";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, ComposedChart, Bar,
} from "recharts";
import {
  Building2, Store, TrendingUp, TrendingDown, AlertTriangle,
  Sparkles, DollarSign, Wallet, Zap, Layers, ChevronRight,
  Activity, Users,
} from "lucide-react";

const HORIZONS = [7, 30, 90, 365];

const T = {
  tr: {
    title: "Enterprise HQ",
    subtitle: "Tüm organizasyonun canlı finansal komuta merkezi.",
    exec: "Yönetici Özeti",
    revToday: "Bugün Ciro", revMonth: "Aylık Ciro", netProfit: "Net Kâr",
    cash: "Nakit", openAp: "Ödenecek Fatura", openAr: "Alınacak Fatura",
    forecast: "30 Gün Tahmin", branches: "Şube", alerts: "Aktif Uyarı",
    health: "Sağlık", brand: "Marka", region: "Bölge", restaurants: "Restoranlar",
    tabOverview: "Genel", tabBrands: "Markalar", tabRestaurants: "Şubeler",
    tabAlerts: "Uyarılar", tabTrends: "Trendler", tabCompare: "Karşılaştırma",
    growth: "Büyüme", revenue: "Ciro", expense: "Gider", profit: "Kâr",
    manager: "Yönetici", status: "Durum", active: "Aktif", inactive: "Pasif",
    open: "Aç", noAlerts: "Uyarı yok.", noRest: "Şube yok.",
    healthGreen: "Sağlıklı", healthYellow: "Dikkat", healthRed: "Kritik",
    bestRev: "En yüksek ciro", worstRev: "En düşük ciro",
    highestProfit: "En yüksek kâr", lowestProfit: "En düşük kâr",
    highestFood: "En yüksek gıda maliyeti", highestLabor: "En yüksek personel maliyeti",
    fastest: "En hızlı büyüyen", drop: "En büyük düşüş", mostEff: "En verimli",
    topCats: "En Yüksek Kategoriler", topSups: "En Yüksek Tedarikçiler",
  },
  de: {
    title: "Enterprise HQ",
    subtitle: "Live-Kommandozentrale für die gesamte Organisation.",
    exec: "Executive Summary",
    revToday: "Heute", revMonth: "Umsatz (Monat)", netProfit: "Nettogewinn",
    cash: "Bargeld", openAp: "Offene AP", openAr: "Offene AR",
    forecast: "30-Tage-Prognose", branches: "Filialen", alerts: "Warnungen",
    health: "Health", brand: "Marke", region: "Region", restaurants: "Filialen",
    tabOverview: "Übersicht", tabBrands: "Marken", tabRestaurants: "Filialen",
    tabAlerts: "Warnungen", tabTrends: "Trends", tabCompare: "Vergleich",
    growth: "Wachstum", revenue: "Umsatz", expense: "Ausgaben", profit: "Gewinn",
    manager: "Manager", status: "Status", active: "Aktiv", inactive: "Inaktiv",
    open: "Öffnen", noAlerts: "Keine Warnungen.", noRest: "Keine Filialen.",
    healthGreen: "Gesund", healthYellow: "Achtung", healthRed: "Kritisch",
    bestRev: "Bester Umsatz", worstRev: "Schlechtester Umsatz",
    highestProfit: "Höchster Gewinn", lowestProfit: "Niedrigster Gewinn",
    highestFood: "Höchste Foodkosten", highestLabor: "Höchste Personalkosten",
    fastest: "Schnellstes Wachstum", drop: "Größter Rückgang", mostEff: "Effizienteste",
    topCats: "Top-Kategorien", topSups: "Top-Lieferanten",
  },
};

const healthCls = (tag) =>
  tag === "green" ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
  tag === "yellow" ? "bg-amber-50 border-amber-200 text-amber-800" :
  "bg-rose-50 border-rose-200 text-rose-900";
const healthDot = (tag) =>
  tag === "green" ? "bg-emerald-500" :
  tag === "yellow" ? "bg-amber-500" : "bg-rose-500";
const sevBadge = (s) =>
  s === "critical" ? "bg-rose-100 text-rose-900 border-rose-300" :
  s === "warning" ? "bg-amber-100 text-amber-900 border-amber-300" :
  "bg-sky-100 text-sky-900 border-sky-300";

function KpiCard({ icon: Icon, label, value, sub, tone = "", testid }) {
  return (
    <div className={`rounded-xl border p-4 bg-background/60 backdrop-blur ${tone}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
      {sub ? <div className="text-xs text-muted-foreground mt-0.5">{sub}</div> : null}
    </div>
  );
}

function HealthDial({ health, testid }) {
  if (!health) return null;
  return (
    <div className={`rounded-xl border-2 p-4 ${healthCls(health.tag)}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide">Organization Health</div>
        <span className={`w-2 h-2 rounded-full ${healthDot(health.tag)}`} />
      </div>
      <div className="mt-1 text-3xl font-bold">{Math.round(health.score)}<span className="text-sm font-normal ml-1">/100</span></div>
      <div className="text-xs mt-1">{health.tag.toUpperCase()}</div>
    </div>
  );
}

export default function HQDashboard() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const denied = !canAccessHQ(user);
  const t = T[lang] || T.tr;
  const [overview, setOverview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [horizon, setHorizon] = useState(30);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [sortKey, setSortKey] = useState("month_revenue");
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    setLoading(true);
    try {
      const [ov, ex] = await Promise.all([
        api.get("/hq/overview"),
        api.get(`/hq/executive-summary?lang=${lang}`),
      ]);
      setOverview(ov.data);
      setSummary(ex.data);
    } catch {
      setOverview(null); setSummary(null);
    } finally {
      setLoading(false);
    }
  };
  const loadTrends = async () => {
    try {
      const { data } = await api.get(`/hq/trends?days=${horizon}`);
      setTrends(data);
    } catch { setTrends(null); }
  };

  useEffect(() => { if (!denied) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [lang, denied]);
  useEffect(() => {
    if (denied) return;
    if (tab === "trends") loadTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, horizon, denied]);

  const t_ = overview?.totals || {};
  const rows = overview?.restaurants || [];
  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = a[sortKey] ?? 0, vb = b[sortKey] ?? 0;
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return arr;
  }, [rows, sortKey, sortDir]);
  const brandName = (bid) => (overview?.brands || []).find(b => b.brand_id === bid)?.name || "—";

  const chartData = useMemo(() => {
    if (!trends?.days) return [];
    return trends.days.map((d, i) => ({
      date: d.slice(5),
      revenue: trends.series.revenue?.[i] || 0,
      expense: trends.series.expense?.[i] || 0,
      profit: trends.series.profit?.[i] || 0,
    }));
  }, [trends]);

  if (denied) return <AccessDenied testid="hq-access-denied" />;

  if (loading) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-4" data-testid="hq-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
      </div>
    );
  }
  if (!overview || overview.counts.restaurants === 0) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="hq-empty">
        <PageHeader title={t.title} description={t.subtitle} />
        <Card className="mt-6"><CardContent className="p-8 text-center text-muted-foreground">
          {lang === "de" ? "Keine Filialen im Zugriff." : "Kapsamınızda şube yok."}
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6" data-testid="hq-page">
      <PageHeader title={t.title} description={t.subtitle} />

      {/* Executive summary card */}
      {summary?.bullets?.length ? (
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-transparent to-transparent" data-testid="hq-exec-summary">
          <CardHeader className="pb-2 flex flex-row items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">{t.exec}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm">
              {summary.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`hq-summary-bullet-${b.type}`}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span>{b.text}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {/* KPI top cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="hq-kpis">
        <KpiCard icon={DollarSign} label={t.revToday} value={money(t_.today_revenue)} testid="hq-kpi-today" />
        <KpiCard icon={TrendingUp} label={t.revMonth} value={money(t_.month_revenue)}
                 sub={`${t.growth}: ${((t_.growth_pct||0)*100).toFixed(1)}%`}
                 testid="hq-kpi-month" />
        <KpiCard icon={Activity} label={t.netProfit} value={money(t_.net_profit)}
                 tone={t_.net_profit < 0 ? "border-rose-300" : ""}
                 testid="hq-kpi-net" />
        <KpiCard icon={Wallet} label={t.cash} value={money(t_.cash)}
                 tone={t_.cash < 0 ? "border-rose-300" : ""}
                 testid="hq-kpi-cash" />
        <KpiCard icon={Zap} label={t.forecast} value={money(t_.forecast_ending)}
                 tone={t_.forecast_ending < 0 ? "border-rose-300" : ""}
                 testid="hq-kpi-forecast" />
        <KpiCard icon={Store} label={t.branches} value={overview.counts.restaurants} testid="hq-kpi-branches" />
        <KpiCard icon={Building2} label={t.brand} value={overview.counts.brands} testid="hq-kpi-brands" />
        <KpiCard icon={AlertTriangle} label={t.alerts} value={overview.counts.open_alerts}
                 tone={overview.counts.open_alerts > 0 ? "border-amber-300" : ""}
                 testid="hq-kpi-alerts" />
        <KpiCard icon={TrendingDown} label={t.openAp} value={money(t_.open_ap)} testid="hq-kpi-ap" />
        <div data-testid="hq-health-wrap"><HealthDial health={overview.health} testid="hq-health" /></div>
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="hq-tabs">
        <TabsList className="rounded-full">
          <TabsTrigger value="overview" data-testid="hq-tab-overview">{t.tabOverview}</TabsTrigger>
          <TabsTrigger value="brands" data-testid="hq-tab-brands">{t.tabBrands}</TabsTrigger>
          <TabsTrigger value="restaurants" data-testid="hq-tab-restaurants">{t.tabRestaurants}</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="hq-tab-alerts">{t.tabAlerts}</TabsTrigger>
          <TabsTrigger value="trends" data-testid="hq-tab-trends">{t.tabTrends}</TabsTrigger>
          <TabsTrigger value="compare" data-testid="hq-tab-compare">{t.tabCompare}</TabsTrigger>
        </TabsList>

        {/* -------- Overview -------- */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card data-testid="hq-brands-card">
              <CardHeader className="pb-2"><CardTitle className="text-base">{t.brand}</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(overview.brands || []).map(b => (
                  <div key={b.brand_id} className="rounded-lg border p-3 flex items-center justify-between" data-testid={`hq-brand-${b.brand_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{b.name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{b.restaurants} {t.branches} · {t.revenue} {money(b.month_revenue)}</div>
                    </div>
                    <Badge className={`${healthCls(b.health.tag)} border`}>{Math.round(b.health.score)}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card data-testid="hq-region-card">
              <CardHeader className="pb-2"><CardTitle className="text-base">{t.region}</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(overview.regions || []).length === 0 && <div className="text-sm text-muted-foreground">—</div>}
                {(overview.regions || []).map(rg => (
                  <div key={rg.region_id} className="rounded-lg border p-3 flex items-center justify-between" data-testid={`hq-region-${rg.region_id}`}>
                    <div>
                      <div className="text-sm font-medium">{rg.name}</div>
                      <div className="text-xs text-muted-foreground">{rg.restaurants} {t.branches} · {money(rg.month_revenue)}</div>
                    </div>
                    <Badge className={`${healthCls(rg.health.tag)} border`}>{Math.round(rg.health.score)}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* -------- Brands -------- */}
        <TabsContent value="brands" className="mt-4">
          <Card data-testid="hq-brands-full">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2">{t.brand}</th>
                    <th className="text-right px-3 py-2">{t.branches}</th>
                    <th className="text-right px-3 py-2">{t.revenue}</th>
                    <th className="text-right px-3 py-2">{t.profit}</th>
                    <th className="text-right px-3 py-2">{t.cash}</th>
                    <th className="text-right px-3 py-2">{t.growth}</th>
                    <th className="text-right px-3 py-2">{t.forecast}</th>
                    <th className="text-center px-3 py-2">{t.health}</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview.brands || []).map(b => (
                    <tr key={b.brand_id} className="border-t" data-testid={`hq-brand-row-${b.brand_id}`}>
                      <td className="px-3 py-2 font-medium">{b.name}</td>
                      <td className="px-3 py-2 text-right">{b.restaurants}</td>
                      <td className="px-3 py-2 text-right">{money(b.month_revenue)}</td>
                      <td className={`px-3 py-2 text-right ${b.net_profit < 0 ? "text-rose-700" : ""}`}>{money(b.net_profit)}</td>
                      <td className="px-3 py-2 text-right">{money(b.cash)}</td>
                      <td className="px-3 py-2 text-right">{((b.growth_pct||0)*100).toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right">{money(b.forecast_ending)}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={`${healthCls(b.health.tag)} border`}>{Math.round(b.health.score)}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Restaurants -------- */}
        <TabsContent value="restaurants" className="mt-4">
          <Card data-testid="hq-restaurants-card">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="hq-restaurants-table">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2 cursor-pointer" onClick={() => setSortKey("name")}>{t.restaurants}</th>
                    <th className="text-left px-3 py-2">{t.brand}</th>
                    <th className="text-right px-3 py-2 cursor-pointer" onClick={() => setSortKey("today_revenue")}>{t.revToday}</th>
                    <th className="text-right px-3 py-2 cursor-pointer" onClick={() => setSortKey("month_revenue")}>{t.revMonth}</th>
                    <th className="text-right px-3 py-2 cursor-pointer" onClick={() => setSortKey("net_profit")}>{t.profit}</th>
                    <th className="text-right px-3 py-2 cursor-pointer" onClick={() => setSortKey("cash")}>{t.cash}</th>
                    <th className="text-right px-3 py-2">{t.forecast}</th>
                    <th className="text-center px-3 py-2">{t.health}</th>
                    <th className="text-center px-3 py-2">{t.alerts}</th>
                    <th className="text-center px-3 py-2">{t.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 && (
                    <tr><td colSpan={10} className="p-6 text-center text-muted-foreground" data-testid="hq-restaurants-empty">{t.noRest}</td></tr>
                  )}
                  {sorted.map(r => (
                    <tr key={r.id} className="border-t hover:bg-muted/20" data-testid={`hq-restaurant-row-${r.id}`}>
                      <td className="px-3 py-2 font-medium">
                        <div>{r.name}</div>
                        <div className="text-[10px] text-muted-foreground">{r.branch_code || ""}</div>
                      </td>
                      <td className="px-3 py-2">{brandName(r.brand_id)}</td>
                      <td className="px-3 py-2 text-right">{money(r.today_revenue)}</td>
                      <td className="px-3 py-2 text-right">{money(r.month_revenue)}</td>
                      <td className={`px-3 py-2 text-right ${r.net_profit < 0 ? "text-rose-700" : ""}`}>{money(r.net_profit)}</td>
                      <td className={`px-3 py-2 text-right ${r.cash < 0 ? "text-rose-700" : ""}`}>{money(r.cash)}</td>
                      <td className={`px-3 py-2 text-right ${r.forecast_ending < 0 ? "text-rose-700" : ""}`}>{money(r.forecast_ending)}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={`${healthCls(r.health.tag)} border`}>{Math.round(r.health.score)}</Badge>
                      </td>
                      <td className="px-3 py-2 text-center">{r.alerts_count > 0 ? <Badge variant="outline" className="border-amber-300 text-amber-800">{r.alerts_count}</Badge> : "—"}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant={r.active ? "default" : "outline"}>{r.active ? t.active : t.inactive}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Alerts -------- */}
        <TabsContent value="alerts" className="mt-4">
          <Card data-testid="hq-alerts-card">
            <CardContent className="p-4 space-y-2">
              {(overview.alerts || []).length === 0 && (
                <div className="text-sm text-muted-foreground" data-testid="hq-alerts-empty">{t.noAlerts}</div>
              )}
              {(overview.alerts || []).map((a, i) => (
                <div key={i} className={`rounded-lg border px-3 py-2 flex items-start gap-2 ${sevBadge(a.severity)}`}
                     data-testid={`hq-alert-${a.code}-${i}`}>
                  <AlertTriangle className="h-4 w-4 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{a.restaurant_name} · {a.code}</div>
                    <div className="text-xs opacity-90">{a.message}</div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Trends -------- */}
        <TabsContent value="trends" className="mt-4 space-y-4">
          <div className="flex gap-2" data-testid="hq-horizon-tabs">
            {HORIZONS.map(h => (
              <Button key={h} size="sm" variant={horizon === h ? "default" : "outline"}
                      onClick={() => setHorizon(h)} data-testid={`hq-horizon-${h}`}>
                {h} {lang === "de" ? "Tage" : "gün"}
              </Button>
            ))}
          </div>
          <Card data-testid="hq-trend-chart">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t.revenue} / {t.expense} / {t.profit}</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" fontSize={10} />
                    <YAxis fontSize={10} />
                    <ReferenceLine y={0} stroke="#c11" strokeDasharray="2 2" />
                    <RTooltip formatter={(v) => money(v)} />
                    <Bar dataKey="revenue" name={t.revenue} fill="#0ea5e9" opacity={0.35} />
                    <Bar dataKey="expense" name={t.expense} fill="#f97316" opacity={0.35} />
                    <Line type="monotone" dataKey="profit" name={t.profit} stroke="#059669" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card data-testid="hq-top-cats">
              <CardHeader className="pb-2"><CardTitle className="text-base">{t.topCats}</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                {(trends?.top_categories || []).slice(0, 8).map((c, i) => (
                  <div key={i} className="flex justify-between border-b py-1">
                    <span className="truncate">{c.name}</span><span className="tabular-nums">{money(c.amount)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card data-testid="hq-top-sups">
              <CardHeader className="pb-2"><CardTitle className="text-base">{t.topSups}</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                {(trends?.top_suppliers || []).slice(0, 8).map((s, i) => (
                  <div key={i} className="flex justify-between border-b py-1">
                    <span className="truncate">{s.vendor}</span><span className="tabular-nums">{money(s.amount)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* -------- Compare -------- */}
        <TabsContent value="compare" className="mt-4">
          <Card data-testid="hq-compare-card">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["best_revenue", t.bestRev],
                ["worst_revenue", t.worstRev],
                ["highest_profit", t.highestProfit],
                ["lowest_profit", t.lowestProfit],
                ["highest_food_cost", t.highestFood],
                ["highest_labor_cost", t.highestLabor],
                ["fastest_growing", t.fastest],
                ["biggest_drop", t.drop],
                ["most_efficient", t.mostEff],
              ].map(([k, label]) => {
                const c = overview.comparison?.[k];
                return (
                  <div key={k} className="rounded-lg border p-3" data-testid={`hq-compare-${k}`}>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
                    <div className="text-sm font-medium">{c?.name || "—"}</div>
                    {c?.value !== undefined && (
                      <div className="text-xs text-muted-foreground">
                        {typeof c.value === "number" && Math.abs(c.value) < 5
                          ? `${(c.value * 100).toFixed(1)}%`
                          : money(c.value)}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
